import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, ProsCons, RichFAQ, RelatedLinks, RichCTA,
} from '../RichBlocks';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * Elbil vs Diesel — bespoke TCO comparison.
 * Figures grounded in public/official sources and conservative estimates:
 * - EV consumption ~0.20–0.22 kWh/km (manufacturer WLTP averages).
 * - Diesel ~5–6 l/100 km (typical mid-size).
 * - Danish elafgift refusion for home charging (Skattestyrelsen scheme).
 * No competitor electricity-seller pricing used.
 */
export default function ElbilVsDiesel({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    {
      question: 'Er en elbil billigere end en dieselbil?',
      answer: 'På driften er elbilen næsten altid billigere – især hvis du lader hjemme og bruger smart, billig strøm. En elbil bruger typisk 0,18–0,22 kWh/km, mens en dieselbil bruger 5–6 liter/100 km. Selv før refusion er energiomkostningen pr. km lavere for elbilen, og med elafgift-refusion og Saving Mode bliver forskellen markant. Anskaffelsesprisen kan dog stadig være højere for elbilen afhængigt af model.',
    },
    {
      question: 'Hvad koster det at lade en elbil hjemme?',
      answer: 'Det afhænger af spotprisen og dit netselskabs tariffer. Hos enkel energi betaler du spotpris uden tillæg, får 10 øre/kWh i laderabat og kan med Saving Mode flytte opladningen til de billigste timer (historisk 15–30% besparelse, ikke garanteret). Derudover kan du få elafgiften refunderet på din hjemmeopladning.',
    },
    {
      question: 'Kan jeg få elafgift refunderet på elbil-opladning?',
      answer: 'Ja. Den danske ordning for refusion af elafgift gælder erhverv og – via en godkendt opgørelse – hjemmeopladning af elbil gennem en operatør. Det kræver en ladeboks med godkendt måling. Hos enkel energi håndteres refusionen automatisk via serviceaftalen, så du ikke skal gøre noget selv.',
    },
    {
      question: 'Hvor meget kan jeg spare ved at skifte fra diesel til elbil?',
      answer: 'Det varierer med kørselsmønster og priser, men på energi alene sparer mange tusindvis af kroner om året ved 15.000–20.000 km/år, fordi strøm pr. km er billigere end diesel – især med refusion og smart opladning. Hertil kommer lavere service- og vedligeholdelsesomkostninger, da elbiler har færre bevægelige dele.',
    },
    {
      question: 'Er elbilen bedre for klimaet end diesel?',
      answer: 'Over bilens levetid har en elbil i Danmark et markant lavere CO2-aftryk end en dieselbil, fordi den danske elproduktion har en høj andel vind og sol. Smart, fleksibel opladning understøtter desuden bedre udnyttelse af vedvarende energi i elnettet.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'energi', label: 'Energi pr. km' },
    { id: 'tabel', label: 'TCO side-om-side' },
    { id: 'lad-billigt', label: 'Lad billigt' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Elbil', path: '/elbil' }, { name: 'Elbil vs diesel', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Elbil vs diesel 2026" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Sammenligning · Økonomi · 2026"
        h1="Elbil vs diesel: Hvad kan bedst betale sig i 2026?"
        intro="Vi sammenligner totaløkonomien – energi pr. kilometer, vedligehold og klima – og viser, hvordan billig, smart hjemmeopladning afgør regnskabet til elbilens fordel."
        badges={['Energi pr. km', 'Drift & vedligehold', 'CO2 over levetid']}
        updated={updated}
        image="https://images.unsplash.com/photo-1593941707874-ef25b8b4a92b?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '~0,20 kWh/km', label: 'typisk elbil-forbrug' },
          { value: '5–6 l/100km', label: 'typisk diesel' },
          { value: '10 øre/kWh', label: 'laderabat hos enkel' },
          { value: 'Op til ~30%', label: 'Saving Mode-besparelse*' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Elbil eller diesel – kort svar">
        <AnswerBox question="Hvad kan bedst betale sig?">
          På <strong>driften</strong> vinder elbilen næsten altid. En elbil bruger typisk <strong>0,18–0,22 kWh/km</strong>, en dieselbil
          <strong> 5–6 liter/100 km</strong>. Energiomkostningen pr. km er lavere for elbilen – og med <strong>elafgift-refusion</strong>,
          spotpris uden tillæg og <strong>Saving Mode</strong> bliver forskellen stor. Diesel kan stadig have lavere anskaffelsespris på visse
          modeller, men over ejertiden trækker driften regnskabet mod elbilen.
        </AnswerBox>
      </ProseSection>

      <ProseSection id="energi" heading="Energi pr. kilometer – hvor pengene ligger" alt>
        <p>
          Den største løbende forskel er energien. En elbil omsætter strøm meget effektivt, mens en forbrændingsmotor taber en stor del af
          energien som varme. Når du oplader hjemme til spotpris – og flytter opladningen til de billige nattetimer med Saving Mode – bliver
          prisen pr. kørt kilometer lav og forudsigelig.
        </p>
        <FactGrid columns={3} facts={[
          { value: 'Effektiv', label: 'Elbilens energiudnyttelse', sub: 'Mindre spild end forbrænding' },
          { value: 'Refusion', label: 'Elafgift på hjemmeladning', sub: 'Håndteres automatisk af enkel' },
          { value: 'Billige timer', label: 'Saving Mode', sub: 'Lader når spotprisen er lavest' },
        ]} />
      </ProseSection>

      <ProseSection id="tabel" heading="Totaløkonomi side om side">
        <div className="my-2">
          <ComparisonTable
            caption="Elbil vs diesel – totaløkonomi"
            columns={[{ name: 'Elbil', highlight: true }, { name: 'Dieselbil' }]}
            rows={[
              { label: 'Energiforbrug', values: ['~0,20 kWh/km', '5–6 l/100 km'] },
              { label: 'Energipris-styring', values: ['Spotpris + Saving Mode', 'Fast pumpepris'] },
              { label: 'Afgift på energi', values: ['Elafgift (kan refunderes)', 'Brændstofafgift (ingen refusion)'] },
              { label: 'Service / vedligehold', values: ['Lavt (få bevægelige dele)', 'Højere (motor, olie, filtre)'] },
              { label: 'CO2 over levetid', values: ['Lavere i DK', 'Højere'] },
              { label: 'Hjemme-"tankning"', values: [true, false] },
              { label: 'Støj og lokal forurening', values: ['Meget lav', 'Højere'] },
            ]}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Tal er typiske gennemsnit (WLTP-forbrug, almindelig dieselforbrug) og tjener til illustration. Din reelle økonomi afhænger af model,
          kørselsmønster og priser. Kilder: producenternes officielle forbrugstal, Nord Pool/Strømligning for elpris, Skattestyrelsens
          elafgifts-refusionsordning.
        </p>
      </ProseSection>

      <div className="container mx-auto px-6 py-10 max-w-4xl">
        <ProsCons
          prosTitle="Fordele ved elbil"
          consTitle="Det skal du være opmærksom på"
          pros={['Lavere energipris pr. km', 'Elafgift kan refunderes på hjemmeladning', 'Færre serviceomkostninger', 'Lavere CO2 i Danmark', 'Hjemmeopladning – ingen tankstation']}
          cons={['Anskaffelsespris kan være højere', 'Kræver ladeboks/ladeadgang', 'Rækkevidde planlægges på lange ture', 'Vinterforbrug er højere']}
        />
      </div>

      <ProseSection id="lad-billigt" heading="Sådan gør du elbilen endnu billigere" alt>
        <p>
          Nøglen til lav elbil-økonomi er <strong>billig, smart opladning</strong>. Hos enkel energi får du spotpris uden tillæg fra 18 kr./md.,
          10 øre/kWh i laderabat og Saving Mode, der automatisk lader i de billigste timer. Serviceaftalen er 0 kr./md. og sørger for, at
          elafgift-refusionen sker automatisk.
        </p>
        <FactGrid columns={3} facts={[
          { value: 'Fra 18 kr./md.', label: 'Spotpris uden tillæg', sub: 'Ingen binding' },
          { value: '0 kr./md.', label: 'Saving Mode + service', sub: 'Inkluderet' },
          { value: '10 øre/kWh', label: 'Laderabat', sub: 'Kun via Saving Mode' },
        ]} />
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elbil', label: 'Elbil-løsning hos enkel' },
        { path: '/portal/smart-charge', label: 'Sådan virker Saving Mode' },
        { path: '/el-fordele#laderabat', label: 'Laderabat – 10 øre/kWh' },
        { path: '/opladere', label: 'Se ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
      ]} />

      <RichCTA
        headline="Gør din elbil billigere at køre"
        subtext="Spotpris uden tillæg, 10 øre laderabat og Saving Mode – alt det, der trækker elbil-regnskabet i din favør."
        primary={{ path: '/elbil', text: 'Se elbil-løsning' }}
        secondary={{ path: '/elaftale', text: 'Se el-aftaler' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}